import { useEffect, useRef, useState } from "react";
import { Button, Select, Spin } from "antd";
import { DownloadOutlined, ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import { RecordModel } from "../../types/core";
import { ancanaApi } from "../../api/ancanaApi";
import RecordDrawer from "../RecordDrawer";


const IframeWithLoader: React.FC<{ src: string; title: string, config: any, recordModel: RecordModel<any> }> = (props) => {
  const { src, title, config, recordModel } = props;
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [refreshKey, setRefreshKey] = useState(0);
  const [recordToEdit, setRecordToEdit] = useState<any>(null);
  const [visibleFields, setVisibleFields] = useState<string[]>();
  const iframeRef = useRef<any>(null);

  useEffect(() => {
    const handleMessage = (event: any) => {
      const allowedOrigins = [
        'http://localhost:3000',
        'https://ancanaliving.com'
      ];

      if (!allowedOrigins.includes(event.origin)) return;

      if (event.data.type === 'EDIT_SECTION') {
        const { slideType, fieldType } = event.data;
        setVisibleFields(fieldType.split(',').map((field: string) => ['title', 'body'].includes(field) ? `${field}__${language}` : field));
        ancanaApi.get(`/sales-material-slides/?config=${config.id}&slide_identifier=${slideType}`).then(res => {
          if (res.data.results.length > 0) {
            setRecordToEdit(res.data.results[0]);
          }
        })
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [language, config]);

  // Construct the URL based on selected language
  const getLocalizedUrl = (baseUrl: string, lang: 'en' | 'es') => {
    if (lang === 'es') {
      // Insert /es before /brochure
      return baseUrl.replace('/brochure/', '/es/brochure/');
    }
    return baseUrl;
  };

  const currentUrl = getLocalizedUrl(src, language);

  const handleRefresh = () => {
    setLoading(true);
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <Spin size="large" />
          <span style={{ color: '#666', fontSize: '16px' }}>Loading brochure...</span>
        </div>
      )}

      {/* Language selector and refresh button */}
      <div
        style={{
          position: 'absolute',
          top: '-48px',
          left: '165px',
          zIndex: 3,
          // backgroundColor: 'white',
          borderRadius: '4px',
          padding: '4px',
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          width: "calc(100% - 230px)"
        }}
        className="justify-between"
      >
        <div className="disp-flex h-stack-strict gap-xs">
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          type="default"
          title="Reload"
          style={{ padding: '4px 8px' }}
        >
          Reload
        </Button>
        <Select
          value={language}
          onChange={(value) => {
            setLoading(true);
            setLanguage(value);
          }}
          style={{ width: 120 }}
          options={[
            { value: 'en', label: '🇬🇧 English' },
            { value: 'es', label: '🇪🇸 Español' }
          ]}
        />
        </div>
        <div className="disp-flex h-stack-strict gap-xs">

        <Button
          onClick={handleRefresh}
          title="Open link"
          style={{ padding: '4px 12px' }}
          href={`https://ancanaliving.com/${language === 'en' ? '' : 'es/'}brochure/${config.resource_slug}`}
          target="_blank"
        >
          <ExportOutlined /> Open link
        </Button>
        <Button
          onClick={handleRefresh}
          title="Download"
          style={{ padding: '4px 12px' }}
          href={`https://ancanaliving.com/api/brochure/${config.resource_slug}/pdf?locale=${language}`}
          target="_blank"
        >
          <DownloadOutlined /> Download
        </Button>
        </div>
      </div>

      <iframe
        ref={iframeRef}
        key={refreshKey}
        src={currentUrl}
        style={{
          width: '100%',
          height: '100%',
          border: '1px solid #d9d9d9',
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.3s ease'
        }}
        title={title}
        onLoad={() => setLoading(false)}
      />

      <RecordDrawer
        onClose={() => setRecordToEdit(null)}
        afterSave={res => {
          iframeRef.current?.contentWindow.postMessage({ type: 'EDIT_SECTION', updatedSlide: res.data }, 'https://ancanaliving.com');
        }}
        extraQueryAppend={`&expand=images&lang=${language}`}
        recordModel={recordModel}
        recordInstance={recordToEdit}
        visibleFields={visibleFields}
      />
    </div>
  );
};

export default IframeWithLoader;
